// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import cx from "classnames";
import { findIndex } from "lodash";
import appConfig from "appConfig";
import { consts as uploaderConsts } from "uploader";
import t from "common/i18n";
import LinkButton from "root/components/LinkButton/LinkButton";
import TabPanel from "tabs/hocs/TabPanel";
import bindRootActions, { types as rootAts } from "root/actions";
import TabFooter from "root/components/TabFooter";
import DropdownList from "root/components/DropdownList/DropdownList";
import ResultImageGrid from "root/components/ResultImageGrid";
import SearchBox, { SEARCH_BOX_MODES } from "root/components/SearchBox";
import { selectImageSearchConfig } from "./selectors";
import bindActions, { types as ats } from "./actions";
import getRightsOptions from "./data/searchRightOptions";
import ICONS from "~/assets/icons/icons";

import "./ImageSearchTab.scss";

type TabData = {
	searchSite: ?string,
	searchSiteIndex: number,
	sites: any[],
	apiKey: string,
	images: Object[],
	searchRight: string,
	isMultiple: boolean,
	selected: string[],
	queries: Object,
	showProgress: boolean,
	searchTerm: string,
	showRights: boolean,
	searchRightIndex: number,
	showOptions: boolean,
	firstSearch: boolean,
	batchStartCounter: number,
};

type Props = {
	tabData: TabData,
	actions: Object,
	rootActions: Object,
	addToUploadQueue: Function,
};

export const getSearchSite = (tabData: TabData) => {
	const site = tabData.searchSite
	|| tabData.sites?.length
		? (~tabData.searchSiteIndex && tabData.sites[tabData.searchSiteIndex]) || tabData.sites[0]
		: "";

	return site !== "all" ? site : "";
};

export const getSiteFilters = (tabData: TabData) => tabData.sites || [];

export const showFilters = (tabData: TabData): boolean => tabData.sites && tabData.sites.length > 1;

export const showRights = (tabData: TabData): boolean => tabData.showRights;

export class ImageSearchTab extends Component<Props> {

	componentDidMount() {
		this.resetSearch();
	}

	componentDidUpdate(prevProps: Props) {
		if (this.props.tabData.selected
			&& this.props.tabData.selected.length
			&& this.props.tabData.batchStartCounter > prevProps.tabData.batchStartCounter) {
			this.clearSelection(); // clear the selection only when batch successfully started
			this.resetSearch();
		}
	}

	onSearchHandler = () => {
		this.clearSelection();

		this.props.actions[ats.DO_IMAGE_SEARCH]({
			apiKey: this.props.tabData.apiKey,
			images: this.props.tabData.images,
			searchRight: this.props.tabData.searchRight,
			searchSite: getSearchSite(this.props.tabData),
			reportError: this.props.rootActions[rootAts.SET_ERROR],
		});
	};

	onSearchTermChangeHandler = (value: string) => this.props.actions[ats.SET_SEARCH_TERM]({ value });

	onSelectionAction = (id: number, e: Object) => this.props.actions[ats.SET_FILE_SELECTION]({
		id,
		e,
		multiple: this.props.tabData.isMultiple,
	});

	onClearSelectedClick = () => this.clearSelection();

	onUploadButtonClick = () => {
		this.props.addToUploadQueue({
			files: this.props.tabData.selected,
		});
	};

	onSiteFilterSelected = (site: string, index: number) => {
		this.props.actions[ats.SET_SEARCH_SITE]({ site, index });
	};

	getSearchPlaceholder = () => {
		let ph = t("image_search.inputPlaceholder");
		const index = ~this.props.tabData.searchSiteIndex ? this.props.tabData.searchSiteIndex : 0;

		const { sites } = this.props.tabData;

		if (sites && sites[index] !== "all") {
			ph = t("image_search.customPlaceholder", { site: sites[index] });
		}

		return ph;
	};

	toggleOptions = () => {
		this.props.actions[ats.SET_OPTIONS_VISIBILITY]({ show: !this.props.tabData.showOptions });
	};

	resetSearch = () => this.props.actions[ats.RESET_SEARCH]();

	getNextPage = () => this.props.tabData.queries?.nextPage?.[0];

	getHasMore = () => {
		const nextPage = !this.props.tabData.showProgress && this.getNextPage();

		return nextPage && nextPage.startIndex < appConfig.google.resultsMaxLimit;
	}

	onLoadMore = () => {
		if (!this.props.tabData.showProgress) {
			const nextPage = this.getNextPage();

			if (this.getHasMore()) { // google only lets us retrieve the first 100 results
				this.props.actions[ats.DO_IMAGE_SEARCH]({
					apiKey: this.props.tabData.apiKey,
					images: this.props.tabData.images,
					query: nextPage.searchTerms,
					start: nextPage.startIndex,
					searchSite: nextPage.siteSearch,
					searchRight: nextPage.rights,
					reportError: this.props.rootActions[rootAts.SET_ERROR],
				});
			}
		}
	};


	clearSelection = () => this.props.actions[ats.CLEAR_IMAGE_SELECTION]();

	renderRightsSelection = () => {
		const rightsList: { text: string, value: string }[] = getRightsOptions();
		const items: string[] = rightsList.map((item) => item.text);
		const selectedItem = rightsList[this.props.tabData.searchRightIndex] || false;

		return <DropdownList items={items}
		                     selectedItem={selectedItem.text}
		                     renderPlaceholder={() => t("image_search.rights")}
		                     onChange={(selectedValue) => {
			                     const index = findIndex(rightsList, ["text", selectedValue]);

			                     this.props.actions[ats.SET_SEARCH_RIGHT]({
				                     index,
				                     right: rightsList[index].value,
			                     });
		                     }}/>;
	};

	renderSitesSelection = () => {
		const sitesList = getSiteFilters(this.props.tabData);
		const selectedSite = sitesList[this.props.tabData.searchSiteIndex] || sitesList[0];

		return (
			<DropdownList items={sitesList}
			              selectedItem={selectedSite}
			              renderInputValue={(site) => `${t("image_search.filters_title")}: ${site}`}
			              onChange={(site) => {
				              const index = sitesList.indexOf(site);
				              this.props.actions[ats.SET_SEARCH_SITE]({ site, index });
			              }}/>
		);
	};

	renderFooter = () => {
		const data = this.props.tabData;
		const selLength = data.selected.length;

		return <TabFooter showSelection
		                  selectionCount={selLength}
		                  clearSelectionHandler={this.onClearSelectedClick}
		                  uploadButtonHandler={this.onUploadButtonClick}/>;
	};


	renderImageGrid = () => !this.props.tabData.firstSearch
		&& <ResultImageGrid images={this.props.tabData.images}
		                    isMultiple={this.props.tabData.isMultiple}
		                    selected={this.props.tabData.selected}
		                    selectionAction={this.onSelectionAction}
							onLoadMore={this.onLoadMore}
							hasMore={this.getHasMore()}
		                    showProgressIndicator={this.props.tabData.showProgress}/>;

	renderOptionsPanel = () => {
		const { tabData } = this.props;
		const showPanel = showRights(tabData) || showFilters(tabData);
		const isOpen = this.props.tabData.showOptions;

		return showPanel
			? <div style={{
				maxWidth: 628, minWidth: 230, zIndex: 3, margin: "0 auto 10px auto",
			}} className="w-100 tr options-panel">
				<div className="flex items-center justify-between mb5">
					<div className="f2 fw4">{t("image_search.main_title")}</div>
					<div className="c-link fw7 f6 inline-flex items-center pointer"
					     onClick={this.toggleOptions} data-test="options-panel">
						<LinkButton
							text={isOpen ? t("image_search.hide_options") : t("image_search.show_options")}
							icon={isOpen ? ICONS.KEYBOARD_UP : ICONS.KEYBOARD_DOWN}
							iconSize={10}
							fontSize={12}/>
					</div>
				</div>
				<div className={cx("relative items-center justify-end", {
					"flex options-panel-open": isOpen,
					dn: !isOpen
				})}>
					{showFilters(tabData)
						? <div className="w-50" data-test="search-by-sites-dropdown">
							{this.renderSitesSelection()}
						</div> : null}
					{showRights(tabData)
						? <div className="pl3 w-50" data-test="usage-rights-dropdown">
							{this.renderRightsSelection()}
						</div> : null}
				</div>
			</div> : <strong className="title">{t("image_search.main_title")}</strong>;
	};

	render() {
		return <div className="content-wrap bg-source-bg bg-source-bg-c google-img-block">
			<div className="holder large-thin-menu-holder">
				<div
					className={`box ${this.props.tabData.firstSearch && !this.props.tabData.images.length ? "centered" : ""}`}>

					{this.renderOptionsPanel()}

					<SearchBox mode={SEARCH_BOX_MODES.EXPANDED}
					           inputPlaceholder={this.getSearchPlaceholder()}
					           searchHandler={this.onSearchHandler}
					           termChangeHandler={this.onSearchTermChangeHandler}
					           value={this.props.tabData.searchTerm}/>

					{this.renderImageGrid()}
				</div>
			</div>

			{this.renderFooter()}
		</div>;
	}
}

export default TabPanel({
	id: uploaderConsts.SOURCES.IMAGE_SEARCH,
})(connect(
	(state) => ({ tabData: selectImageSearchConfig(state) }),
	(dispatch: Function) => ({
		actions: bindActions(dispatch),
		rootActions: bindRootActions(dispatch),
	}),
)(ImageSearchTab));
